import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';

import { BasicLayout, scrollUp, useStepper } from '@monorepo/common';

import { Step1 } from './steps/Step1';
import { Step2 } from './steps/Step2';
import { FinalStep } from './components/FinalStep';
import { useAppSelector } from '../../store/hooks';
import { usePaymentIntentForUpgradeMutation } from '../../services/serviceJobData';
import { buildUpgradeIntentBody, interpretIntentResponse, GENERIC_INTENT_ERROR } from '../../components/JobPayment';
import { ACCESS_REMOVED_MESSAGE, isForbidden } from '../../utils/jobAccessError';
import { setPageTitle } from '../../utils/setPageTitle';
import { Hosts } from '../../constants/hostName';
import { MetaDescription } from '../../components/MetaDescription';
import { getEditJobPath } from '../../utils/jobAccountRoutes';

// interface IUpgradeListingProps {}
export const UpgradeListing = () => {
  const [titleBrowserPage, setTitleBrowserPage] = useState<string>('Jobs');
  const { guid } = useParams<{ guid: string }>();

  const { siteId } = useAppSelector(({ pathnameSettings }) => ({
    siteId: pathnameSettings.siteNumber
  }));

  // One-shot PaymentIntent (Upgrade has no re-pricing). Created when the payment
  // step is reached (Step1 → Step2). The 403 (access withdrawn) handling that used
  // to live on Step2's on-submit intent call now lives here on the intent path.
  const [getPaymentIntent] = usePaymentIntentForUpgradeMutation();
  const [reachedPayment, setReachedPayment] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);

  const createIntent = useCallback(async () => {
    setIntentError(null);
    const result = await getPaymentIntent(buildUpgradeIntentBody({ siteId: siteId.toString(), eguid: guid }));
    if ('data' in result) {
      const outcome = interpretIntentResponse(result.data);
      setClientSecret(outcome.clientSecret);
      setIntentError(outcome.error);
    } else {
      setClientSecret(null);
      if (isForbidden(result.error)) {
        scrollUp();
        setIntentError(ACCESS_REMOVED_MESSAGE);
      } else {
        setIntentError(GENERIC_INTENT_ERROR);
      }
    }
  }, [getPaymentIntent, siteId, guid]);

  useEffect(() => {
    if (reachedPayment) {
      createIntent();
    }
  }, [reachedPayment, createIntent]);

  const { putSteps, onNext, onBack, onComplete, renderStepper, StepsButtons } = useStepper({
    orientation: 'vertical',
    leftInfoPanel: (
      <Typography variant="h3">
        Upgrade Job Listing:
      </Typography>
    ),
    finalComponent: <FinalStep
      editJobPath={getEditJobPath(siteId, guid)}
    />
  });

  const steps = useMemo(() => [
    {
      label: 'Step 1: Listing Type',
      content: (
        <Step1
          Nav={StepsButtons}
          onNext={() => {
            setReachedPayment(true);
            onNext();
          }}
        />
      )
    },
    {
      label: 'Step 2: Payment Information',
      content: (
        <Step2
          onBack={onBack}
          Nav={StepsButtons}
          onComplete={onComplete}
          clientSecret={clientSecret}
          intentError={intentError}
          retryIntent={createIntent}
        />
      )
    },
  ], [StepsButtons, clientSecret, intentError, createIntent]);

  useEffect(() => {
    setPageTitle({
      firstTitle: 'Upgrade Job Listing',
      siteNumber: siteId || Hosts.BEVNET,
      setTitle: setTitleBrowserPage
    });
  }, [siteId]);

  useEffect(() => {
    // Re-publish steps when intent state changes — useStepper renders a snapshot
    // from putSteps, not live children, so Step2 needs the fresh secret/error.
    putSteps(steps);
  }, [clientSecret, intentError, createIntent]);

  return (
    <BasicLayout
      dataCy="UpgradeListing"
      helmet={MetaDescription({ title: titleBrowserPage })}
    >
      {renderStepper}
    </BasicLayout>
  );
};
